import { useState, type FormEvent } from "react";
import FileUploadField from "./FileUploadField";
import HoneypotField from "./HoneypotField";
import EnquiryConfirmation from "./EnquiryConfirmation";
import useEnquiryForm from "../hooks/useEnquiryForm";
import { submitB2bEnquiry } from "../utils/b2bEnquiry";

const BUSINESS_TYPES = ["Hotel", "Restaurant", "Caterer", "Cloud Kitchen", "Retail Chain", "Other"];

export default function B2bEnquiryForm() {
  const { status, referenceNumber, submit, reset } = useEnquiryForm("b2b");
  const [honeypot, setHoneypot] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [estimatedMonthlyVolume, setEstimatedMonthlyVolume] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [rfqDoc, setRfqDoc] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    submit(() =>
      submitB2bEnquiry({
        companyName,
        businessType,
        estimatedMonthlyVolume,
        deliveryCity,
        contactEmail,
        rfqDocumentName: rfqDoc ?? undefined,
      })
    );
  };

  if (status === "success" && referenceNumber) {
    return <EnquiryConfirmation type="b2b" referenceNumber={referenceNumber} onSendAnother={reset} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <HoneypotField value={honeypot} onChange={setHoneypot} />

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Company name</span>
          <input
            required
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Business type</span>
          <select
            required
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          >
            <option value="" disabled>
              Select one
            </option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Estimated monthly volume</span>
          <input
            required
            type="text"
            placeholder="e.g. 500 kg/month"
            value={estimatedMonthlyVolume}
            onChange={(e) => setEstimatedMonthlyVolume(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Preferred delivery city/zone</span>
          <input
            required
            type="text"
            value={deliveryCity}
            onChange={(e) => setDeliveryCity(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Email</span>
          <input
            required
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
      </div>

      <FileUploadField
        label="RFQ / requirements document (optional, PDF or DOCX, max 10MB)"
        accept="application/pdf,.docx"
        maxSizeMB={10}
        onFileSelected={setRfqDoc}
      />

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-50"
      >
        {status === "loading" ? "Submitting…" : "Submit Enquiry"}
      </button>
      {status === "error" && <p className="text-center text-xs text-coral-500">Something went wrong — please try again.</p>}
    </form>
  );
}
